/**
 * Small pieces the Probe area's pages and sidebar share: the outcome and
 * rating glyphs, a page's column, its properties and its sections, the day
 * format, and the empty, loading and failed states every page draws the
 * same way.
 *
 * @module components/apps/probe/ProbeParts
 */
import type { ProbeAreaAssessment, ProbeOutcome } from "@amoreg/contracts";
import { Query } from "@lucasmarkes/hairline/react";
import { RotateCwIcon } from "lucide-react";
import { useEffect, useState, type ReactNode, type Ref } from "react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { HairlineArt } from "../../ui/hairline-art";
import { Skeleton } from "../../ui/skeleton";
import { SeverityGlyph as FindingSeverityGlyph } from "../../regulation/finding/findingGlyphs";
import { SEVERITY_ORDER, type Severity } from "../../regulation/finding/findingItem";
import { cn } from "~/lib/utils";
import { ASSESSMENT_LABELS, OUTCOME_LABELS } from "./probeArea";

const OUTCOME_VARIANT = { strong: "success", mixed: "warning", weak: "error" } as const;

const isSeverity = (severity: string): severity is Severity =>
  (SEVERITY_ORDER as ReadonlyArray<string>).includes(severity);

/** A finding's severity on the desks: the findings pages' own glyph, named in its label and tooltip. */
export function SeverityGlyph({
  severity,
  className,
}: {
  readonly severity: string;
  readonly className?: string;
}) {
  return (
    <FindingSeverityGlyph
      severity={isSeverity(severity) ? severity : null}
      {...(className !== undefined ? { className } : {})}
    />
  );
}

/** The interview's outcome, as the debrief left it. */
export function OutcomeBadge({
  outcome,
  className,
}: {
  readonly outcome: ProbeOutcome;
  readonly className?: string;
}) {
  return (
    <Badge variant={OUTCOME_VARIANT[outcome]} className={cn("rounded-full px-1.5", className)}>
      {OUTCOME_LABELS[outcome]}
    </Badge>
  );
}

const OUTCOME_LEVEL: Readonly<Record<ProbeOutcome, 1 | 2 | 3>> = { weak: 1, mixed: 2, strong: 3 };

/**
 * A rating as one glyph in T3 Code's sidebar status style: a ring, ticked
 * when strong, half filled when it held up in part, marked when weak. Ink
 * like the text around it; only weak takes a colour, as the one to act on.
 */
export function RatingGlyph({
  level,
  className,
}: {
  readonly level: 1 | 2 | 3;
  readonly className?: string;
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden
      className={cn(
        "size-3.5 shrink-0",
        level === 1 ? "text-destructive" : "text-muted-foreground dark:text-foreground/60",
        className,
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      data-probe-rating={level}
    >
      <circle cx="8" cy="8" r="6.25" />
      {level === 3 ? <path d="m5.6 8.2 1.7 1.7 3.2-3.4" /> : null}
      {level === 2 ? <path d="M8 3.75a4.25 4.25 0 0 1 0 8.5z" fill="currentColor" stroke="none" /> : null}
      {level === 1 ? (
        <>
          <path d="M8 5v3.4" />
          <circle cx="8" cy="10.9" r="0.4" fill="currentColor" />
        </>
      ) : null}
    </svg>
  );
}

/** The interview's outcome as its glyph alone, named for screen readers. */
export function OutcomeGlyph({ outcome }: { readonly outcome: ProbeOutcome }) {
  return (
    <span className="inline-flex items-center" data-probe-outcome-glyph={outcome}>
      <RatingGlyph level={OUTCOME_LEVEL[outcome]} />
      <span className="sr-only">{OUTCOME_LABELS[outcome]}</span>
    </span>
  );
}

const ASSESSMENT_LEVEL: Readonly<Record<ProbeAreaAssessment, 1 | 2 | 3>> = {
  weak: 1,
  adequate: 2,
  strong: 3,
};

/** An area's rating as its glyph, named for screen readers and on hover. */
export function AssessmentGlyph({
  assessment,
  className,
}: {
  readonly assessment: ProbeAreaAssessment;
  readonly className?: string;
}) {
  return (
    <span
      className={cn("inline-flex size-4 shrink-0 items-center justify-center", className)}
      title={ASSESSMENT_LABELS[assessment]}
      data-probe-assessment={assessment}
    >
      <RatingGlyph level={ASSESSMENT_LEVEL[assessment]} />
      <span className="sr-only">{ASSESSMENT_LABELS[assessment]}</span>
    </span>
  );
}

/** The interview's outcome as a status, as its list shows it: its glyph and its word. */
export function OutcomeStatus({
  outcome,
  className,
}: {
  readonly outcome: ProbeOutcome;
  readonly className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-2 text-foreground", className)}
      data-probe-outcome={outcome}
    >
      <span aria-hidden className="inline-flex size-4 items-center justify-center">
        <RatingGlyph level={OUTCOME_LEVEL[outcome]} />
      </span>
      {OUTCOME_LABELS[outcome]}
    </span>
  );
}

/** `12 Oct`, or `12 Oct 2025` outside this year. */
export const interviewDay = (iso: string, nowMs: number = Date.now()): string => {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "";
  const date = new Date(time);
  const sameYear = date.getFullYear() === new Date(nowMs).getFullYear();
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

/** A day as `interviewDay` writes it, for an ISO time or a bare `2026-09-18` (read as that local day). */
export const probeDay = (date: string, nowMs: number = Date.now()): string =>
  interviewDay(/^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00` : date, nowMs);

/** `12 Oct 2026, 14:05`. */
export const interviewDateTime = (iso: string): string => {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "";
  return new Date(time).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** A centred note for a page with nothing to show yet. */
export function ProbeEmpty({
  title,
  children,
  action,
}: {
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center" data-probe-empty="">
      <HairlineArt figure={Query} className="mb-4 w-48 sm:w-56" />
      <p className="text-sm font-medium text-foreground">{title}</p>
      {children ? (
        <p className="mt-1 max-w-sm text-[13px] text-muted-foreground text-balance">{children}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** A read that failed, with the way to try again. */
export function ProbeLoadError({
  what,
  onRetry,
  className,
}: {
  readonly what: string;
  readonly onRetry: () => void;
  readonly className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-16 text-center",
        className,
      )}
      data-probe-error=""
    >
      <p className="text-[13px] text-muted-foreground">{what} couldn&rsquo;t be loaded.</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RotateCwIcon className="size-3.5" />
        Try again
      </Button>
    </div>
  );
}

/** Placeholder rows while a list loads. */
export function ProbeRowsSkeleton({ rows = 5 }: { readonly rows?: number }) {
  return (
    <div className="flex flex-col" aria-hidden data-probe-skeleton="">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-4 border-b border-border/50 px-4 py-3 sm:px-6"
        >
          <Skeleton className="h-3.5 flex-1" style={{ maxWidth: `${60 - (index % 3) * 12}%` }} />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-10" />
        </div>
      ))}
    </div>
  );
}

/** Quiet text that keeps 4.5:1 on both themes. */
export const QUIET = "text-muted-foreground dark:text-foreground/70";

/** A Probe page's column: the 48rem of Chat's composer, centred, as the review sets it. */
export const PROBE_COLUMN = "mx-auto w-full max-w-[calc(48rem+2.5rem)] px-3 sm:px-5";

/** A property on one line, as Linear's issue header sets them: a muted icon and its value. */
export function ProbeProperty({
  icon,
  title,
  children,
}: {
  readonly icon?: ReactNode;
  /** What the value is, on hover. */
  readonly title?: string;
  readonly children: ReactNode;
}) {
  return (
    <p
      title={title}
      className="flex h-7 min-w-0 items-center gap-1.5 px-2 text-foreground tabular-nums [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground"
    >
      {icon}
      <span className="truncate">{children}</span>
    </p>
  );
}

/** A titled list on a Probe page: its name and bare count over a hairline, rows under it. */
export function ProbeSection({
  title,
  count,
  action,
  children,
  ...data
}: {
  readonly title: string;
  readonly count?: ReactNode;
  /** At the heading's end. */
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly [data: `data-${string}`]: string | undefined;
}) {
  return (
    <section aria-label={title} className="flex flex-col" {...data}>
      <h2 className="flex h-8 items-center gap-2 border-b border-border/60 px-2 text-[13px] font-medium text-foreground">
        {title}
        {count !== undefined ? <span className={cn("font-normal tabular-nums", QUIET)}>{count}</span> : null}
        {action ? <span className="ms-auto flex items-center">{action}</span> : null}
      </h2>
      {children}
    </section>
  );
}

/** Rows a Probe list shows before its "Show all". */
const PROBE_LIST_CAP = 5;

/** A list row's box and type: Home's and the Briefing's lists keep one rhythm. */
export const PROBE_LIST_ROW = "min-h-9 px-3 py-2 text-[13px] leading-5";

/** A list row that opens something. */
export const PROBE_LIST_LINK =
  "outline-none hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset";

/** "Show all" at a list's foot, its words lined up with the rows' text after their 16px lead. */
const PROBE_SHOW_ALL = cn(
  "flex h-9 w-full items-center ps-10 pe-3 text-start text-[13px] hover:text-foreground",
  PROBE_LIST_LINK,
  QUIET,
);

/**
 * A list as Linear sets one: its heading (a 16px lead, the name, a bare
 * count) over a hairline, hairline rows, one muted dash when empty, and a
 * "Show all" under it. The box around it is the page's.
 */
export function ProbeList({
  id,
  icon,
  title,
  count,
  rows,
  more,
  listRef,
  ...data
}: {
  readonly id: string;
  /** The heading's lead: a glyph, or a muted icon. */
  readonly icon?: ReactNode;
  readonly title: string;
  readonly count: ReactNode;
  /** The `<li>` rows. */
  readonly rows: ReadonlyArray<ReactNode>;
  readonly more?: ReactNode;
  readonly listRef?: Ref<HTMLElement>;
  readonly [data: `data-${string}`]: string | undefined;
}) {
  return (
    <section ref={listRef} aria-labelledby={id} className="flex min-w-0 scroll-mt-4 flex-col" {...data}>
      <h2
        id={id}
        className="flex h-9 shrink-0 items-center gap-3 border-b border-border/70 px-3 text-[13px] font-medium text-foreground"
      >
        {icon === undefined ? null : (
          <span className="flex w-4 shrink-0 justify-center [&_svg]:size-3.5 [&_svg]:shrink-0">{icon}</span>
        )}
        <span className="flex min-w-0 items-center gap-2">
          {title}
          <span className={cn("font-normal tabular-nums", QUIET)}>{count}</span>
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className={cn("flex h-9 items-center px-3 text-[13px]", QUIET)}>
          <span aria-hidden>–</span>
          <span className="sr-only">None</span>
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border/50">{rows}</ul>
      )}
      {more === undefined || more === null ? null : <div className="border-t border-border/50">{more}</div>}
    </section>
  );
}

/** The first `PROBE_LIST_CAP` of a list, or every one once opened, with the toggle to say which. */
export function useProbeCap<T>(
  items: ReadonlyArray<T>,
  /** Open it whenever this turns true, as the address can ask. */
  openWhen = false,
): {
  readonly shown: ReadonlyArray<T>;
  readonly more: ReactNode;
} {
  const [open, setOpen] = useState(openWhen);
  useEffect(() => {
    if (openWhen) setOpen(true);
  }, [openWhen]);
  return {
    shown: open ? items : items.slice(0, PROBE_LIST_CAP),
    more:
      items.length > PROBE_LIST_CAP ? (
        <button type="button" className={PROBE_SHOW_ALL} aria-expanded={open} onClick={() => setOpen((was) => !was)}>
          {open ? "Show fewer" : `Show all ${items.length}`}
        </button>
      ) : null,
  };
}
